import { useState } from 'react';
import { api, money, useQuery } from '../api.js';
import { Drawer, ErrorNote, Pane, Panes, Pill, Spinner, Stat, useToast } from '../ui.js';
import type { Invoice } from '../../../shared/types.js';

interface Arrears {
  studentId: number;
  name: string;
  email: string;
  unpaidCount: number;
  unpaidCents: number;
  oldestDueOn: string;
  daysOverdue: number;
  lastReminderAt: number | null;
  overThreshold: boolean;
}

interface Reminder {
  studentId: number; name: string; email: string;
  unpaidCount: number; unpaidCents: number; status: string; emailId: number;
}

interface Data {
  invoices: Invoice[];
  summary: {
    billedCents: number; flights: number;
    outstandingCents: number; outstandingCount: number;
    overdueCents: number; overdueCount: number;
    payableCents: number; payableHours: number;
  };
  earningsOnly: boolean;
  split: number;
  canManage: boolean;
  canPay: boolean;
  /** Accounts carrying an unpaid balance, worst first. */
  arrears: Arrears[];
  /** More unpaid lessons than this and the account is chased automatically. */
  chaseThreshold: number;
  canChase: boolean;
  /** False when no mail transport is configured, which the screen says out loud. */
  mailDelivers: boolean;
  mailTransport: 'resend' | 'webhook' | 'none';
  /** Reminders the sweep sent as this screen was opened. */
  justSent: Reminder[];
  month: string;
  note: string;
}

const sinceLabel = (at: number): string => {
  const days = Math.floor((Date.now() - at) / 86400000);
  if (days <= 0) return 'today';
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

export function Billing(): JSX.Element {
  const { data, loading, error, reload } = useQuery<Data>('/billing');
  const [openId, setOpenId] = useState<number | null>(null);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const s = data.summary;

  return (
    <div className="page">
      <div className="stat-grid">
        {data.earningsOnly ? (
          <>
            <Stat label="My instruction" value={money(s.billedCents)} sub={`${s.flights} flights in ${data.month}`} />
            <Stat label="My share" value={money(s.payableCents)} sub={`${s.payableHours} hrs at the ${Math.round(data.split * 100)}% split`} />
            <Stat label="Awaiting payment" value={money(s.outstandingCents)} sub={`${s.outstandingCount} invoices`} />
            <Stat label="Over 30 days" value={money(s.overdueCents)} sub={`${s.overdueCount} invoices`} />
          </>
        ) : (
          <>
            <Stat label={`Billed in ${data.month}`} value={money(s.billedCents)} sub={`${s.flights} flights`} />
            <Stat label="Outstanding" value={money(s.outstandingCents)} sub={`${s.outstandingCount} invoices`} />
            <Stat label="Over 30 days" value={money(s.overdueCents)} sub={`${s.overdueCount} invoices`} />
            <Stat label="Instruction payable" value={money(s.payableCents)} sub={`${s.payableHours} hrs`} />
          </>
        )}
      </div>

      {!data.earningsOnly && (
        <Arrears
          accounts={data.arrears}
          threshold={data.chaseThreshold}
          canChase={data.canChase}
          delivers={data.mailDelivers}
          justSent={data.justSent}
          onSent={reload}
        />
      )}

      <div className="card">
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Invoice</th><th>Student</th><th>Flight</th>
                {!data.earningsOnly && <th>Aircraft</th>}
                <th>Instruction</th>
                <th>{data.earningsOnly ? 'My share' : 'Total'}</th>
                <th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {data.invoices.length === 0 && (
                <tr><td colSpan={8} className="empty">No invoices yet. Close a flight out to raise one.</td></tr>
              )}
              {data.invoices.map((v) => (
                <tr key={v.id}>
                  <td className="bold">{v.number}</td>
                  <td>{v.studentName}</td>
                  <td>{v.flightLabel}</td>
                  {!data.earningsOnly && <td className="num">{money(v.aircraftCents)}</td>}
                  <td className="num">{money(v.instructionCents)}</td>
                  <td className="num bold">{money(data.earningsOnly ? v.instructorShareCents : v.totalCents)}</td>
                  <td>
                    <Pill>
                      {v.status === 'sent' && v.daysOverdue > 0
                        ? `${v.daysOverdue > 30 ? 'Overdue' : 'Unpaid'} · ${v.daysOverdue} days`
                        : v.status}
                    </Pill>
                  </td>
                  <td className="num"><button className="btn btn--link" onClick={() => setOpenId(v.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="note">{data.note}</div>
      {loading && <div className="small muted" style={{ padding: 8 }}>Refreshing…</div>}

      {openId && (
        <InvoiceDrawer
          id={openId}
          canPay={data.canPay}
          canManage={data.canManage}
          canChase={data.canChase}
          onClose={() => setOpenId(null)}
          onChanged={() => { setOpenId(null); reload(); }}
        />
      )}
    </div>
  );
}

/**
 * The accounts that have run up a balance. Anything past the threshold is
 * chased on its own when this screen opens; the button is for the account that
 * has not crossed it yet, or the one that needs asking again sooner.
 */
function Arrears({ accounts, threshold, canChase, delivers, justSent, onSent }: {
  accounts: Arrears[]; threshold: number; canChase: boolean; delivers: boolean;
  justSent: Reminder[]; onSent: () => void;
}): JSX.Element | null {
  const toast = useToast();
  const [busy, setBusy] = useState<number | null>(null);

  if (accounts.length === 0) return null;

  const send = async (a: Arrears) => {
    setBusy(a.studentId);
    try {
      const sent = await api.post<Reminder>(`/billing/reminders/${a.studentId}`, {});
      toast.ok(
        delivers ? `Reminder sent to ${a.name}` : `Reminder recorded for ${a.name}`,
        delivers
          ? `${sent.email} · ${sent.unpaidCount} unpaid lessons.`
          : `No mail transport is configured, so it is filed to ${sent.email} rather than delivered.`,
      );
      onSent();
    } catch (err) {
      toast.error('Could not send that reminder', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between', padding: '14px 16px 0' }}>
        <strong>Accounts outstanding</strong>
        <span className="small muted">
          More than {threshold} unpaid lessons is chased automatically, at most once a week.
        </span>
      </div>

      {justSent.length > 0 && (
        <div className="note" style={{ margin: '10px 16px 0' }}>
          {delivers ? 'Reminder sent to ' : 'Reminder recorded for '}
          {justSent.map((r) => r.name).join(', ')} on opening this screen.
        </div>
      )}

      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Student</th><th>Unpaid</th><th>Balance</th><th>Oldest due</th><th>Last reminded</th><th />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.studentId}>
                <td className="bold">{a.name}</td>
                <td>
                  <Pill tone={a.overThreshold ? 'red' : undefined}>
                    {a.unpaidCount} {a.unpaidCount === 1 ? 'lesson' : 'lessons'}
                  </Pill>
                </td>
                <td className="num bold">{money(a.unpaidCents)}</td>
                <td>{a.daysOverdue > 0 ? `${a.oldestDueOn} · ${a.daysOverdue} days ago` : a.oldestDueOn}</td>
                <td className="muted">{a.lastReminderAt ? sinceLabel(a.lastReminderAt) : 'never'}</td>
                <td className="num">
                  {canChase && (
                    <button className="btn btn--link" disabled={busy === a.studentId || !a.email}
                      onClick={() => send(a)}>
                      {busy === a.studentId ? 'Sending…' : 'Send reminder'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!delivers && (
        <div className="note">
          No mail transport is configured, so reminders are recorded against the student's
          account and posted to their portal rather than emailed. Set <code>MAIL_WEBHOOK_URL</code>
          {' '}to have them delivered.
        </div>
      )}
    </div>
  );
}

function InvoiceDrawer({ id, canPay, canManage, canChase, onClose, onChanged }: {
  id: number; canPay: boolean; canManage: boolean; canChase: boolean;
  onClose: () => void; onChanged: () => void;
}): JSX.Element {
  const { data } = useQuery<Invoice>(`/billing/${id}`);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const act = async (path: string, body: unknown, message: string) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.ok(message);
      onChanged();
    } catch (err) {
      toast.error('That did not work', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const payable = data && data.status !== 'paid' && data.status !== 'void';

  return (
    <Drawer
      title={data ? `Invoice ${data.number}` : 'Invoice'}
      subtitle={data ? `${data.studentName} · issued ${data.issuedOn} · due ${data.dueOn}` : undefined}
      onClose={onClose}
      footer={data && (
        <>
          {canPay && payable && (
            <button className="btn btn--primary" disabled={busy} onClick={() => act(`/billing/${id}/pay`, {}, 'Payment recorded')}>
              Mark paid
            </button>
          )}
          {canChase && (
            <button className="btn" disabled={busy}
              onClick={() => act(`/billing/${id}/send`, {}, `Invoice ${data.number} sent to ${data.studentName}`)}>
              Email invoice
            </button>
          )}
          {canChase && payable && (
            <button className="btn" disabled={busy}
              onClick={() => act(`/billing/reminders/${data.studentId}`, {}, `Reminder sent to ${data.studentName}`)}>
              Send reminder
            </button>
          )}
          {canManage && payable && (
            <button className="btn btn--danger" disabled={busy}
              onClick={() => act(`/billing/${id}/void`, { reason: 'Voided from the billing screen' }, 'Invoice voided')}>
              Void
            </button>
          )}
        </>
      )}
    >
      {!data && <Spinner />}
      {data && (
        <Panes>
          <Pane title="Line items" flush>
            <div className="row" style={{ gap: 6, padding: '12px 24px 10px' }}>
              <Pill>{data.status}</Pill>
              {data.daysOverdue > 0 && <Pill tone="red">{data.daysOverdue} days past due</Pill>}
            </div>
            {data.lines?.map((l) => (
              <div className="kv" key={l.id}>
                <span style={{ fontWeight: 400, textAlign: 'left' }}>{l.description}</span>
                <span>{money(l.amountCents)}</span>
              </div>
            ))}
          </Pane>

          <Pane title="Totals" flush>
            <div className="kv"><span>Aircraft</span><span>{money(data.aircraftCents)}</span></div>
            <div className="kv"><span>Instruction</span><span>{money(data.instructionCents)}</span></div>
            <div className="kv" style={{ fontSize: 16 }}>
              <span>Total</span><span style={{ fontWeight: 700 }}>{money(data.totalCents)}</span>
            </div>
            <div className="kv"><span>Instructor share</span><span>{money(data.instructorShareCents)}</span></div>
            <div className="kv"><span>Instructor</span><span>{data.instructorName}</span></div>
            {data.paidOn && <div className="kv"><span>Paid</span><span>{data.paidOn}</span></div>}
            <div style={{ padding: '14px 24px 0' }} className="small muted">
              <span style={{ textWrap: 'pretty' }}>
                Priced from the Hobbs and the block times recorded on the flight. Correcting the
                Hobbs on a completed reservation re-prices this invoice.
              </span>
            </div>
          </Pane>
        </Panes>
      )}
    </Drawer>
  );
}
